import { useNavigate } from 'react-router';
import { css } from '@emotion/react';
import DateUtils from '@helpers/date';
import { useAppTranslation } from '@hooks';
import { routes } from '@navigation/routes';
import { useState } from 'react';
import { SegmentType } from '@pages/types';
import { getStateKeyByValue } from '@utils';
import { SegmentDefType } from '@definitions';
import { DeleteModal } from '@components/modal';
import { StateTagSegment } from '@views/segment/shared';
import {
  Button,
  Col,
  Content,
  Icon,
  Row,
  Space,
  Title,
  Text,
  ActionModal,
  useDisclosure,
  FormItem,
  IconSearch,
  TextInput,
  BG_COLORS,
  Form,
  AvatarIcon,
  AvatarIcons,
  IconData,
  colors,
} from 'common-components';

import { store } from '@/store';

type HeaderPropsType = {
  segment: SegmentType
};

export const HeaderSegment = ({ segment }: HeaderPropsType) => {
  const updateSegmentAction = store.use.updateSegmentAction().call;
  const deleteSegmentsAction = store.use.deleteSegmentsAction().call;

  const isLoadingDeleteSegmentsAction = store.use.deleteSegmentsAction().status === 'LOADING';
  const [updating, setUpdating] = useState(false);

  const { translate } = useAppTranslation();
  const navigate = useNavigate();
  const useDisclosureDeleteSegmentsModal = useDisclosure();

  const [segmentName, setSegmentName] = useState<string>(segment.name);
  const [iconData, setIconData] = useState<IconData | null>(segment.icon as IconData);

  const {
    open: openUpdateModal,
    isOpen: isOpenUpdateModal,
    close: closeUpdateModal,
  } = useDisclosure();

  const handleCancel = () => closeUpdateModal();

  const handleConfirm = () => handleCancel();

  const texts = [
    {
      type: 'singlePageTitle',
      content: segment.name,
      style: { color: colors.gray700 },
    },
    {
      type: 'tablePageSubtitle',
      content: `${translate('segment.created')} ${DateUtils.formatDate(
        segment.creationDate,
      )} ${translate('segment.by')} ${segment.idUser}`,
    },
  ];

  function getActionButtonsForAddSegment() {
    return [
      {
        text: translate('createSegment.back'),
        onClick: closeUpdateModal,
      },
      {
        type: 'primary',
        disabled: !iconData || !segmentName || updating,
        text: translate('createSegment.next'),
        onClick: async () => {
          setUpdating(true);
          await updateSegmentAction(
            {
              idSegment: segment.idSegment,
              icon: iconData,
              name: segmentName,
            },
            () => closeUpdateModal(),
          );
          closeUpdateModal();
          setUpdating(false);
        },
      },
    ];
  }

  const onDeleteSegmentActionMutation = async () => {
    deleteSegmentsAction(
      {
        idSegments: [segment.idSegment],
      },
      () => {
        setTimeout(() => useDisclosureDeleteSegmentsModal.close(), 200);
        navigate(routes.segments.navigate());
      },
    );
  };

  return (
    <Content>
      <Row
        justify={'space-between'}
        align={'top'}
        wrap={false}
        css={css`
          margin: 24px 0;
          gap: 16px;
        `}
      >
        <Col
          css={css`
            flex: 1;
            min-width: 0;
          `}
        >
          <Row
            align={'middle'}
            wrap={false}
            css={css`
              gap: 20px;
            `}
          >
            <Col>
              <Button
                size="sm"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <Icon icon="FlipBackward" size={20} />
              </Button>
            </Col>
            <AvatarIcon
              icon={segment?.icon?.iconName as AvatarIcons}
              style={{ backgroundColor: segment?.icon?.iconColor }}
              size={'xl'}
            />
            <Col>
              {texts.map((text, index) => {
                return (
                  <Title
                    key={`title-${text.content}-${index}`}
                    preset={text.type as any}
                    style={text.style}
                  >
                    {text.content}
                  </Title>
                );
              })}
            </Col>
            <Col>
              <StateTagSegment
                state={segment.segmentType}
                style={{ fontSize: 14, lineHeight: '20px', padding: '2px 10px 2px 8px' }}
              />
            </Col>
          </Row>
        </Col>
        <Row>
          <Space>
            {segment?.segmentType !== getStateKeyByValue(SegmentDefType, 'certification') && (
              <Button
                onClick={useDisclosureDeleteSegmentsModal.open}
                type="danger"
                iconLeft="Trash02"
              >
                {translate('segments.deleteSegment.button')}
              </Button>
            )}

            <Button danger={false} onClick={openUpdateModal} iconLeft="Edit02">
              {translate('segments.headerButtons.modify')}
            </Button>
          </Space>
          <ActionModal
            open={isOpenUpdateModal}
            onOk={handleConfirm}
            onConfirm={handleConfirm}
            css={css`
              min-width: 700px;
            `}
            title={translate('segment.updateModal.title')}
            onCancel={handleCancel}
            actionButtons={getActionButtonsForAddSegment()}
          >
            <Text size="sm">{translate('segment.updateModal.subtitle')}</Text>
            <Form
              css={css`
                margin-top: 20px;
              `}
            >
              <Row
                css={css`
                  gap: 15px;
                `}
              >
                <FormItem label={translate('createSegment.icon')}>
                  <IconSearch value={iconData} bgColors={BG_COLORS} onSubmit={setIconData} />
                </FormItem>
                <FormItem
                  css={css`
                    flex-grow: 1;
                  `}
                  label={translate('createSegment.name')}
                >
                  <TextInput
                    value={segmentName}
                    onChange={(e) => setSegmentName(e.currentTarget.value)}
                    placeholder={translate('createSegment.exampleEnglishLevel1')}
                  />
                </FormItem>
              </Row>
            </Form>
          </ActionModal>
          {useDisclosureDeleteSegmentsModal.isOpen && (
            <DeleteModal
              close={useDisclosureDeleteSegmentsModal.close}
              isOpen={useDisclosureDeleteSegmentsModal.isOpen}
              title={translate('segments.deleteSegment.modal.title')}
              label={translate('segments.deleteSegment.modal.description')}
              isLoading={isLoadingDeleteSegmentsAction}
              onDeleteMutation={onDeleteSegmentActionMutation}
            />
          )}
        </Row>
      </Row>
    </Content>
  );
};
